import {
  exploreSamplesResponseSchema,
  type ExploreSamplesResponse,
} from "@sage/shared";

export type ExploreFetchParams = {
  page?: number;
  limit?: number;
  verdict?: string;
  q?: string;
  sort?: string;
  order?: "asc" | "desc";
};

export type ExploreFetchFailure = {
  kind: "unauthorized" | "network_error";
  message: string;
  requestId?: string;
};

export type ExploreFetchResult =
  | { ok: true; data: ExploreSamplesResponse }
  | { ok: false; error: ExploreFetchFailure };

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "https://api.sage.ironwort.me";

export async function fetchExploreSamples(
  token: string | null,
  params: ExploreFetchParams = {}
): Promise<ExploreFetchResult> {
  try {
    const url = new URL(`${API_BASE_URL}/v1/explore/samples`);

    if (params.page !== undefined) url.searchParams.set("page", String(params.page));
    if (params.limit !== undefined) url.searchParams.set("limit", String(params.limit));
    if (params.verdict) url.searchParams.set("verdict", params.verdict);
    if (params.q) url.searchParams.set("q", params.q);
    if (params.sort) url.searchParams.set("sort", params.sort);
    if (params.order) url.searchParams.set("order", params.order);

    const response = await fetch(url.toString(), {
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    const body: unknown = await response.json().catch(() => null);

    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        return {
          ok: false,
          error: {
            kind: "unauthorized",
            message: "Sign in to browse the sample catalog.",
          },
        };
      }
      return {
        ok: false,
        error: {
          kind: "network_error",
          message: "The explore API request failed.",
        },
      };
    }

    if (!isRecord(body) || body.ok !== true || !isRecord(body.data)) {
      return {
        ok: false,
        error: {
          kind: "network_error",
          message: "The explore API returned an unexpected response.",
        },
      };
    }

    const parsed = exploreSamplesResponseSchema.safeParse(body.data);
    if (!parsed.success) {
      return {
        ok: false,
        error: {
          kind: "network_error",
          message: "The explore API response did not match the expected shape.",
        },
      };
    }

    return { ok: true, data: parsed.data };
  } catch {
    return {
      ok: false,
      error: {
        kind: "network_error",
        message: "Unable to reach the explore API. Check the network or API base URL.",
      },
    };
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
